// src/components/crm/customers/CustomerList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Filter, PlusCircle, ChevronRight, User, 
  Mail, Phone, Globe, Building, X, Users, Briefcase 
} from 'lucide-react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';

const CustomerList = () => {
  const { currentUser } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [error, setError] = useState(null);

  // Fetch user's businesses
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchUserBusinesses = async () => {
      try {
        const businessesRef = collection(db, 'businesses');
        const businessesQuery = query(
          businessesRef,
          where('createdBy', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(businessesQuery);
        const businesses = [];
        
        querySnapshot.forEach(doc => {
          businesses.push({
            id: doc.id,
            name: doc.data().name || 'Unnamed Business'
          });
        });
        
        setUserBusinesses(businesses);
        
        // If there's only one business, auto-select it
        if (businesses.length === 1 && !selectedBusinessId) {
          setSelectedBusinessId(businesses[0].id);
        }
      } catch (error) {
        console.error('Error fetching user businesses:', error);
        setError('Failed to load businesses. Please try again.');
      }
    };
    
    fetchUserBusinesses();
  }, [currentUser]);

  // Fetch customers when selectedBusinessId changes
  useEffect(() => {
    if (!currentUser || !selectedBusinessId) {
      setIsLoading(false);
      return;
    }
    
    const fetchCustomers = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Create a query to fetch customers for the selected business
        const customersRef = collection(db, 'customers');
        const customersQuery = query(
          customersRef,
          where('businessId', '==', selectedBusinessId),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(customersQuery);
        const customersList = [];
        
        querySnapshot.forEach(doc => {
          customersList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setCustomers(customersList);
        console.log(`Fetched ${customersList.length} customers for business ${selectedBusinessId}`);
      } catch (error) {
        console.error('Error fetching customers:', error);
        setError('Failed to load customers. Please try again.');
        setCustomers([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCustomers();
  }, [currentUser, selectedBusinessId]);

  // Get filtered customers
  const getFilteredCustomers = () => {
    let filtered = customers;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(customer => 
        (customer.name && customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.contactName && customer.contactName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.category && customer.category.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(customer => customer.category === filterCategory);
    }
    
    return filtered;
  };

  // Get unique categories from customers
  const categories = ['all', ...new Set(customers
    .filter(customer => customer.category)
    .map(customer => customer.category))];

  // Format date function
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Convert Firestore timestamp to JS Date
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <div>
      {/* Business Selector */}
      <div className="mb-6">
        <label htmlFor="businessId" className="block text-sm font-medium text-gray-700 mb-2">
          Select Business
        </label>
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Briefcase size={18} className="text-gray-400" />
          </div>
          <select
            id="businessId"
            className="pl-10 block w-full rounded-lg border border-gray-300 py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
            value={selectedBusinessId}
            onChange={(e) => setSelectedBusinessId(e.target.value)}
          >
            <option value="">Select Business</option>
            {userBusinesses.map((business) => (
              <option key={business.id} value={business.id}>{business.name}</option>
            ))}
          </select>
        </div>
        {userBusinesses.length === 0 && (
          <p className="mt-1 text-amber-600 text-sm">
            You don't have any businesses yet. Please create a business first.
          </p>
        )}
      </div>

      {/* Search and Actions Bar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Search by name, contact, email, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-3">
          <select 
            className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 bg-white focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.filter(c => c !== 'all').map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          <Link
            to="/crm/customers/new"
            className={`px-4 py-2.5 rounded-lg ${
              selectedBusinessId ? 'bg-[#190192] text-white hover:bg-[#2C0DB5]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            } flex items-center`}
          >
            <PlusCircle size={18} className="mr-2" />
            Add Customer
          </Link>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 text-red-700">
          <p className="font-medium">Error</p>
          <p>{error}</p>
        </div>
      )}

      {/* Select Business Prompt */}
      {!selectedBusinessId && !isLoading && (
        <div className="bg-blue-50 rounded-lg py-10 px-6 text-center">
          <Briefcase size={48} className="mx-auto text-blue-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Business</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Please select a business from the dropdown above to view customers.
          </p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Empty State */}
          {selectedBusinessId && customers.length === 0 ? (
            <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
              <Users size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                You haven't added any customers to this business yet. Click the 'Add Customer' button to get started.
              </p>
              <Link
                to="/crm/customers/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-[#2C0DB5]"
              >
                <PlusCircle size={16} className="mr-2" />
                Add Your First Customer
              </Link>
            </div>
          ) : selectedBusinessId && getFilteredCustomers().length === 0 ? (
            <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
              <Users size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matching customers</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                We couldn't find any customers matching your search criteria. Try adjusting your search or filters.
              </p>
              <button
                onClick={() => { setSearchTerm(''); setFilterCategory('all'); }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          ) : selectedBusinessId ? (
            /* Customers List/Table */
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredCustomers().map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-800">
                              {customer.type === 'business' ? 
                                <Building size={20} /> : 
                                <User size={20} />
                              }
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                              {customer.type === 'business' && customer.contactName && (
                                <div className="text-sm text-gray-500">Contact: {customer.contactName}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Mail size={14} className="mr-1 text-gray-400" /> {customer.email}
                          </div>
                          {customer.phone && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <Phone size={14} className="mr-1 text-gray-400" /> {customer.phone}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {customer.category && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              {customer.category}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.status?.charAt(0).toUpperCase() + customer.status?.slice(1) || 'Active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(customer.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/crm/customers/${customer.id}`}
                            className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
                          >
                            View <ChevronRight size={16} className="ml-1" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CustomerList;